/**
 * Doporučené místo, chosen by rule (the owner, 30 October 2026).
 *
 * "If it's a big car, they should park outside, because going up 7–8 floors in
 * the garage is not comfortable. If it's a garage booking, it has to go to the
 * floor M." Until now the dispatcher guessed it by hand together with the
 * KeyCafe code; the codes are automatic now and the place was left empty, so
 * every customer's check-in said "7. patro".
 *
 * Step 1 of the plan in the call doc: rules by car only. No counting of free
 * places yet (step 2), and an ordinary car in an ordinary booking gets no rule
 * — the dispatcher still decides those, as before.
 *
 *   big car (van, minibus, pick-up — not SUVs)         → outside_exit ("Venku u výjezdu";
 *       the owner's office, 30 Oct 2026: "we don't have spaces u vjezdu, we always use u výjezdu")
 *   Garáž booking (type `regular`)                      → M
 *   a big car on a Garáž booking                        → outside_exit: the ramps are the
 *       problem the owner named, and the car is put in the hall at Tuchoměřice anyway
 *
 * `car` is free text typed by the customer or the desk ("VW Multivan", "vw t6
 * bílá", "Mercedes V"). Three months of production bookings (Aug–Oct 2026,
 * 11 186): 463 match the list below (about 5 a day), 1 627 are Garáž (about 18 a
 * day), 51 are both. The list is deliberately narrow — a false "outside" costs
 * a kerbside place, a missed van costs a phone call — and it is the one place to
 * add a model the managers name.
 *
 * Zero runtime imports, so a harness can execute it directly (like sites.ts).
 */
/** Vans, minibuses and pick-ups by model. SUVs are not on it: the owner's choice, 30 Oct 2026. */
export const BIG_CAR = new RegExp([
    // Vans and minibuses.
    'vito', 'viano', 'sprinter', 'multivan', 'transporter', 'caravelle', 'california', 'crafter',
    'id\\.? ?buzz', 'tourneo custom', 'transit(?! connect| courier)', 'proace(?! city)', 'vivaro', 'zafira life',
    'traveller', 'jumpy', 'space ?tourer', 'trafic', 'master', 'ducato', 'jumper', 'boxer',
    'staria', 'h-?1', 'dodávka', 'dodavka', 'minibus', 'bus',
    // VW T4–T7 only when it reads as the van, not a Volvo engine ("XC60 T5").
    '(?:vw|volkswagen|wv)\\s*t ?[4-7](?:\\.1)?', '^t[4-7]$',
    // Mercedes V-Class in its spellings.
    '(?:mercedes(?:[- ]benz)?|mb)\\s+v(?:\\s*-?\\s*(?:klasse|class|trieda|třída))?', 'v-?\\s?(?:klasse|class)',
    // Pick-ups.
    'hilux', 'amarok', 'navara', 'l200', 'ford ranger', '^ranger',
    // RAM (Dodge RAM 1500 …): the manager, 30 Oct 2026 21:39.
    'ram',
    // SUVs are not on the list (the owner, 30 Oct 2026 17:06: "vans, minibuses, pick-ups" only).
]
    .map((w) => (w.startsWith('^') ? w : `\\b${w}\\b`))
    .join('|'), 'i');
export function isBigCar(car) {
    const s = (car ?? '').trim().replace(/\s+/g, ' ');
    return s !== '' && BIG_CAR.test(s);
}
/** The rule's place for a booking, or null when no rule applies (the dispatcher decides). */
export function autoTargetFor(car, type) {
    if (isBigCar(car))
        return { target: 'outside_exit', reason: 'velké auto' };
    if (type === 'regular')
        return { target: 'M', reason: 'garážová rezervace' };
    return null;
}
/* ------------------------------------------------------------------ *
 * Ordinary cars: the zone with the most room (the owner, 30 Oct 2026 17:15:
 * "yes, build it now"). Step 2 of the plan in the call doc, first version.
 * ------------------------------------------------------------------ */
/** Our own Europort places (the owner, 30 Oct 2026): kept here as literals so this module stays
 *  import-free; verify-sites asserts they equal CAPACITY.europort. Other floors are lent to us by
 *  other tenants in busy weeks and are not counted. */
export const EUROPORT_ZONES = [
    { zone: 'venku', places: 31, value: 'outside_exit', label: 'Venku u výjezdu' },
    { zone: '7', places: 34, value: '7', label: '7. patro' },
    { zone: 'M', places: 18, value: 'M', label: 'M' },
    { zone: '8', places: 4, value: '8', label: '8. patro' },
];
/** Which of our zones a parking / parkingtarget value is, or null (another floor, not Europort). */
export function europortZoneOf(spot) {
    const s = (spot ?? '').trim();
    if (s === 'outside_entry' || s === 'outside_exit')
        return 'venku';
    if (s === '7' || s === 'M' || s === '8')
        return s;
    return null;
}
/** Places kept free for cars that are not ours (strangers park on our places): 10 %, rounded up. */
export function strangerMargin(places) {
    return Math.ceil(places * 0.1);
}
/**
 * The place for an ordinary car, given how many of our cars each zone is expected to hold when it
 * arrives. Venku and the 7th floor share the load by how much room each has left (the
 * dispatchers' "one outside, one inside"); M is kept for Garáž bookings and taken only when both
 * are full; the 8th floor (4 places) last. All full: the 7th floor, the floor other tenants lend
 * next to — the dispatcher sees it and can move it.
 */
export function zoneTargetFor(load) {
    const free = (z) => z.places - strangerMargin(z.places) - (load[z.zone] ?? 0);
    const byName = new Map(EUROPORT_ZONES.map((z) => [z.zone, z]));
    const first = ['venku', '7']
        .map((n) => byName.get(n))
        .filter((z) => free(z) > 0)
        .sort((a, b) => free(b) / b.places - free(a) / a.places || free(b) - free(a))[0];
    const pick = first ?? ['M', '8'].map((n) => byName.get(n)).find((z) => free(z) > 0);
    if (pick)
        return { target: pick.value, reason: `nejvíc volných míst (${pick.label})`, full: false };
    return { target: '7', reason: 'vše plné — 7. patro', full: true };
}
